"use client";

import { CloudinaryImage } from "@/client/components/common/CloudinaryImage";
import { useDebounce } from "@/client/components/hooks/useDebounce";
import { cn } from "@/client/lib/classnames";
import {
  AB_FLAGSHIP_URL,
  getFlagshipArticleUrl,
  getFlagshipCollectionUrl,
  getFlagshipExhibitionUrl,
  getWalletPath,
} from "@/client/lib/links";
import {
  FullSiteSearchDocument,
  GetAllLiveArticlePagesQuery,
  GetAllLiveExhibitionPagesQuery,
  UserSearchResultFragment,
} from "@/gql/graphql";
import { getGraphQlClient } from "@/shared/artblocks";
import { getBasePath } from "@/shared/config";
import { ArtistItemInfo } from "@/shared/nav";
import * as Popover from "@radix-ui/react-popover";
import { useQuery } from "@tanstack/react-query";
import { CommandLoading, useCommandState } from "cmdk";
import Fuse from "fuse.js";
import { AnimatePresence, motion } from "motion/react";
import { useRouter } from "next/router";
import React, { Suspense } from "react";
import { normalize } from "viem/ens";
import { useEnsAddress } from "wagmi";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./Command";
import { LoadingIndicator, XIcon } from "./icons";

const HASURA_MAX_CACHE_AGE = 300; // 5 Minutes

function getPrefixedLinkPath(path: string) {
  return `${getBasePath()}${path}`;
}

const getProjectSearchFallback = (id: string) => {
  if (id === "0xab0000000000aa06f89b268d604a9c1c41524ac6-501") {
    return getPrefixedLinkPath(
      "/images/poetics/thumbs/whileTrue(fabric)-thumb.png"
    );
  }
  if (id === "0xab0000000000aa06f89b268d604a9c1c41524ac6-500") {
    return getPrefixedLinkPath(
      "/images/poetics/thumbs/INK(fragrances)-thumb.png"
    );
  }
  if (id === "0xab0000000000aa06f89b268d604a9c1c41524ac6-502") {
    return getPrefixedLinkPath(
      "/images/poetics/thumbs/boxLightStudies(sculpture)-thumb.png"
    );
  }
  return getPrefixedLinkPath("/square-placeholder.jpg");
};

const SearchSection = ({
  title,
  children,
  className,
}: {
  title: string;
  children: React.ReactElement | React.ReactElement[];
  className?: string;
}) => {
  return (
    <CommandGroup className={cn("w-full", className)} heading={title}>
      {children}
    </CommandGroup>
  );
};

type FullSiteSearchProps = {
  artistData: ArtistItemInfo[] | undefined;
  articleData: GetAllLiveArticlePagesQuery | undefined;
  exhibitionData: GetAllLiveExhibitionPagesQuery | undefined;
  onFocus: () => void;
  onBlur: () => void;
};

const FullSiteSearch = ({
  articleData,
  artistData,
  exhibitionData,
  onFocus,
  onBlur,
}: FullSiteSearchProps): React.ReactElement => {
  const [searchTerm, setSearchTerm] = React.useState<string>("");
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const debouncedSearchTerm = useDebounce<string>(searchTerm, 500);

  const onDismiss = React.useCallback(() => {
    setSearchTerm("");
    searchInputRef.current?.blur();
  }, []);

  const handleSearchItemClicked = (searchResult: string) => {
    onDismiss();
  };

  React.useEffect(() => {
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        onDismiss();
      }
    });
  }, [searchTerm, onDismiss]);

  const [placeholder, setPlaceholder] = React.useState("Search");
  React.useEffect(() => {
    const changePlaceholder = () => {
      const width = document.documentElement.clientWidth;
      if (width > 768) {
        setPlaceholder("Search for artists, collections, or articles");
      } else {
        setPlaceholder("Search");
      }
    };

    changePlaceholder();

    if (typeof window !== "undefined") {
      window.addEventListener("resize", changePlaceholder, true);
    }

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("resize", changePlaceholder, true);
      }
    };
  }, []);

  return (
    <Popover.Root
      open={Boolean(searchTerm)}
      onOpenChange={(open) => {
        if (!open) {
          onDismiss();
        }
      }}
    >
      <Popover.Anchor asChild>
        <motion.div
          className="relative flex gap-md"
          animate={{ gap: searchTerm ? "24px" : 0 }}
        >
          <div className="flex-1">
            <CommandInput
              placeholder={placeholder}
              className={cn("flex-1")}
              value={searchTerm}
              onInput={(e) => setSearchTerm(e.currentTarget.value)}
              onFocus={onFocus}
              onBlur={onBlur}
              ref={searchInputRef}
            />
          </div>
          <AnimatePresence>
            {searchTerm ? (
              <motion.button
                className="md:right-sm md:absolute md:top-1/2 md:-translate-y-1/2"
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: "auto" }}
                exit={{ opacity: 0, width: 0 }}
              >
                <XIcon className="fill-on-surface w-sm h-sm" />
              </motion.button>
            ) : null}
          </AnimatePresence>
        </motion.div>
      </Popover.Anchor>
      <Suspense
        fallback={
          <div className="flex justify-center w-full">
            <LoadingIndicator className="block w-10 h-10 fill-outline-variant" />
          </div>
        }
      >
        <FullSiteSearchResults
          exhibitionData={exhibitionData}
          searchTerm={debouncedSearchTerm}
          articleData={articleData}
          artistData={artistData}
          onSearchItemClick={handleSearchItemClicked}
        />
      </Suspense>
    </Popover.Root>
  );
};

function FullSiteSearchWrapper({
  artistData,
  articleData,
  exhibitionData,
  onFocus,
  onBlur,
}: FullSiteSearchProps): React.ReactElement {
  return (
    <Command shouldFilter={false}>
      <FullSiteSearch
        artistData={artistData}
        articleData={articleData}
        exhibitionData={exhibitionData}
        onFocus={onFocus}
        onBlur={onBlur}
      />
    </Command>
  );
}
export { FullSiteSearchWrapper as FullSiteSearch };

const SEARCH_LIMIT = 5;

function FullSiteSearchResults({
  artistData,
  articleData,
  exhibitionData,
  onSearchItemClick,
}: Omit<FullSiteSearchProps, "onFocus" | "onBlur"> & {
  searchTerm: string;
  onSearchItemClick: (searchResult: string) => void;
}): React.ReactElement {
  const router = useRouter();

  const artistPagefuseOptions = {
    shouldSort: true,
    isCaseSensitive: false,
    keys: ["name", "slug", "projects.name"],
  };

  const articlePagefuseOptions = {
    shouldSort: true,
    isCaseSensitive: false,
    keys: ["attributes.title", "attributes.slug"],
  };

  const exhibitionFuseOptions = {
    shouldSort: true,
    isCaseSensitive: false,
    keys: [
      "attributes.Title",
      "attributes.slug",
      "attributes.Collections.Collection.Project.name",
      "attributes.Collections.Collection.Project.artist_name",
    ],
  };

  const artistFuse = new Fuse(artistData ?? [], artistPagefuseOptions);

  const articleFuse = new Fuse(
    articleData?.articles?.data ?? [],
    articlePagefuseOptions
  );

  const exhibitionFuse = new Fuse(
    exhibitionData?.exhibitions?.data ?? [],
    exhibitionFuseOptions
  );

  const searchTerm: string = useCommandState((state) => state.search);

  // If the search term can be normalized as an ENS address,
  // we attempt to resolve it to an address and use that for the
  // search. This allows us to search for users by ENS name.
  let normalizedEns: string | undefined = undefined;
  try {
    normalizedEns = normalize(searchTerm);
  } catch (error) {
    // No-op, we assume the search term just isn't an ENS name
  }

  const { data: ensAddress } = useEnsAddress({
    name: normalizedEns,
    query: {
      enabled: normalizedEns !== undefined,
    },
  });

  const res = useQuery({
    queryKey: ["full-site-search", searchTerm, ensAddress],
    queryFn: () => {
      const client = getGraphQlClient();
      return client.request(FullSiteSearchDocument, {
        limit: SEARCH_LIMIT,
        searchTerm: `%${searchTerm}%`,
        resolvedEnsAddress: ensAddress?.toLowerCase(),
        includeResolvedEnsLookup: Boolean(ensAddress),
        projectWhere: {},
        ttl: HASURA_MAX_CACHE_AGE,
      });
    },
  });

  const artistPages = artistFuse.search(searchTerm).slice(0, SEARCH_LIMIT);
  const articlePages = articleFuse.search(searchTerm).slice(0, SEARCH_LIMIT);
  const exhibitionPages = exhibitionFuse
    .search(searchTerm)
    .slice(0, SEARCH_LIMIT);
  const projects = res.data?.search_projects ?? [];
  // Combine and dedup all users
  const searchUsers = res.data?.search_users ?? [];
  const resolvedEnsUsers = res.data?.user_profiles ?? [];
  const allUsers = Array.from(new Set([...resolvedEnsUsers, ...searchUsers]));

  return (
    <Popover.Portal>
      <AnimatePresence>
        {searchTerm ? (
          <Popover.Content
            forceMount
            className="z-40 w-full border bg-surface border-outline-variant rounded-xs"
            sideOffset={18}
            style={{
              width: "var(--radix-popover-trigger-width)",
            }}
            onOpenAutoFocus={(e) => {
              e.preventDefault();
            }}
            asChild
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ visualDuration: 0.4, bounce: 0 }}
            >
              <CommandList className="pb-sm max-h-[calc(var(--radix-popover-content-available-height)_-_16px)]">
                {(() => {
                  // Only show loading if there are no results and the query is still fetching
                  if (
                    !projects.length &&
                    !allUsers.length &&
                    !artistPages.length &&
                    !articlePages.length &&
                    !exhibitionPages.length &&
                    res.isLoading
                  ) {
                    return (
                      <CommandLoading className="flex justify-center w-full pt-md pb-xs">
                        <LoadingIndicator className="block w-10 h-10" />
                      </CommandLoading>
                    );
                  }

                  if (
                    searchTerm &&
                    !projects.length &&
                    !allUsers.length &&
                    !artistPages.length &&
                    !articlePages.length &&
                    !exhibitionPages.length
                  ) {
                    return (
                      <CommandEmpty>
                        <div>
                          <p className="font-medium text-center text-headline text-on-surface mb-sm">
                            No items found
                          </p>
                          <p className="text-center text-body mb-md text-on-surface">
                            Try broadening your search or discovering something
                            new
                          </p>
                          <a
                            href={`${AB_FLAGSHIP_URL}/discover`}
                            className="flex items-center justify-center"
                          >
                            <p className="text-center text-body rounded-xs bg-primary/10 px-sm py-xs">
                              Browse collections
                            </p>
                          </a>
                        </div>
                      </CommandEmpty>
                    );
                  }
                  return (
                    <>
                      {/* Show small loading indicator in addition to results when there
                       are some results but others are still loading */}
                      <AnimatePresence>
                        {res.isLoading ? (
                          <motion.div
                            className="absolute top-sm right-sm"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                          >
                            <LoadingIndicator className="block h-sm w-sm fill-outline-variant" />
                          </motion.div>
                        ) : null}
                      </AnimatePresence>
                      {exhibitionPages.length ? (
                        <SearchSection title="EXHIBITIONS">
                          <div>
                            {exhibitionPages.map((exhibitionPage) => {
                              const exhibition = exhibitionPage.item.attributes;
                              const title = exhibition?.Title;
                              const key = `exhibition-${title}`;
                              const headerImageUrl =
                                exhibition?.HeaderImage?.data?.attributes?.url;

                              return (
                                <CommandItem
                                  key={key}
                                  value={key}
                                  onSelect={() => {
                                    onSearchItemClick(key);
                                    window.location.href =
                                      getFlagshipExhibitionUrl(
                                        exhibition?.slug || ""
                                      );
                                  }}
                                >
                                  <a>
                                    <div className="flex flex-row items-center gap-x-md">
                                      <div className="relative flex flex-shrink-0 h-2xl w-2xl">
                                        {headerImageUrl ? (
                                          <CloudinaryImage
                                            alt={`${exhibition?.Title} thumbnail`}
                                            src={headerImageUrl}
                                            fill
                                            className="object-cover"
                                          />
                                        ) : (
                                          <CloudinaryImage
                                            alt={`${exhibition?.Title} thumbnail`}
                                            src={getPrefixedLinkPath(
                                              "/16-9-placeholder.jpg"
                                            )}
                                            fill
                                            className="object-cover"
                                            sizes={`10vw`}
                                          />
                                        )}
                                      </div>
                                      <div className="flex flex-col gap-y-xs">
                                        <p className="text-body">
                                          {exhibition?.Title}
                                        </p>
                                      </div>
                                    </div>
                                  </a>
                                </CommandItem>
                              );
                            })}
                          </div>
                        </SearchSection>
                      ) : null}
                      {projects.length ? (
                        <SearchSection title="COLLECTIONS">
                          <div className="flex flex-col gap-md">
                            {projects.map((project) => {
                              const aspectRatio = Number(
                                project.aspect_ratio ?? 1
                              );

                              return (
                                <CommandItem
                                  key={`project-${project.id}`}
                                  value={`project-${project.id}`}
                                  onSelect={() => {
                                    onSearchItemClick(`project-${project.id}`);
                                    window.location.href =
                                      getFlagshipCollectionUrl(project.slug);
                                  }}
                                >
                                  <a>
                                    <div className="flex flex-row items-center flex-shrink-0 gap-x-md">
                                      <div className="relative flex items-center justify-center h-2xl w-2xl">
                                        <div
                                          className={cn(
                                            "shadow-artwork relative",
                                            {
                                              "w-full": aspectRatio >= 1,
                                              "h-full": aspectRatio < 1,
                                            }
                                          )}
                                          style={{
                                            aspectRatio: aspectRatio,
                                          }}
                                        >
                                          {project.tokens?.[0]?.media_url ? (
                                            <CloudinaryImage
                                              alt={`${project.name} thumbnail`}
                                              disableAutoFormat={
                                                project.disable_auto_image_format ??
                                                false
                                              }
                                              src={
                                                project.tokens?.[0]?.media_url
                                              }
                                              fill
                                              className="object-contain"
                                            />
                                          ) : project.oob_tokens?.[0]
                                              ?.media_url ? (
                                            <CloudinaryImage
                                              alt={`${project.name} sample thumbnail`}
                                              disableAutoFormat={
                                                project.disable_auto_image_format ??
                                                false
                                              }
                                              src={
                                                project.oob_tokens?.[0]
                                                  ?.media_url
                                              }
                                              fill
                                              className="object-contain"
                                            />
                                          ) : (
                                            <CloudinaryImage
                                              alt={`${project.name} thumbnail`}
                                              src={getProjectSearchFallback(
                                                project.id
                                              )}
                                              fill
                                              className="object-cover"
                                              sizes={`10vw`}
                                            />
                                          )}
                                        </div>
                                      </div>
                                      <div className="flex flex-col gap-y-2xs">
                                        <p className="text-body">
                                          {project.name}
                                        </p>
                                        <p className="text-on-surface-variant text-body">
                                          {project.artist_name}
                                        </p>
                                      </div>
                                    </div>
                                  </a>
                                </CommandItem>
                              );
                            })}
                          </div>
                        </SearchSection>
                      ) : null}
                      {artistPages.length ? (
                        <SearchSection title="ARTISTS">
                          {artistPages.map((page) => {
                            return (
                              <CommandItem
                                key={`artist-${page.item.slug}`}
                                value={`artist-${page.item.slug}`}
                                onSelect={() => {
                                  onSearchItemClick(`artist-${page.item.slug}`);
                                  window.location.href = page.item.url;
                                }}
                              >
                                <a>
                                  <div className="flex items-center gap-md">
                                    <div className="relative flex-shrink-0 overflow-hidden rounded-full h-2xl w-2xl">
                                      {page.item.avatarUrl ? (
                                        <CloudinaryImage
                                          alt={`${page.item.name} thumbnail`}
                                          src={page.item.avatarUrl}
                                          fill
                                          className="object-cover"
                                          sizes={`10vw`}
                                        />
                                      ) : (
                                        <CloudinaryImage
                                          alt={`${page.item.name} thumbnail`}
                                          src={getPrefixedLinkPath(
                                            "/square-placeholder.jpg"
                                          )}
                                          fill
                                          className="object-cover"
                                          sizes={`10vw`}
                                        />
                                      )}
                                    </div>
                                    <p className="text-body">
                                      {page.item.name}
                                    </p>
                                  </div>
                                </a>
                              </CommandItem>
                            );
                          })}
                        </SearchSection>
                      ) : null}
                      {articlePages.length ? (
                        <SearchSection title="ARTICLES">
                          {articlePages.map((page) => (
                            <CommandItem
                              key={`article-${page.item.attributes?.slug}`}
                              value={`article-${page.item.attributes?.slug}`}
                              onSelect={() => {
                                onSearchItemClick(
                                  `article-${page.item.attributes?.slug}`
                                );
                                window.location.href = getFlagshipArticleUrl(
                                  page.item.attributes?.slug || ""
                                );
                              }}
                            >
                              <a>
                                <div className="flex flex-row items-center gap-x-md">
                                  <div className="relative flex-shrink-0 h-2xl w-2xl">
                                    {page.item.attributes?.thumbnail?.data
                                      ?.attributes?.url ? (
                                      <CloudinaryImage
                                        alt={`${page.item.attributes?.title} thumbnail`}
                                        src={
                                          page.item.attributes?.thumbnail?.data
                                            ?.attributes?.url
                                        }
                                        fill
                                        className="object-cover"
                                        sizes={`10vw`}
                                      />
                                    ) : (
                                      <CloudinaryImage
                                        alt={`${page.item.attributes?.title} thumbnail`}
                                        src={getPrefixedLinkPath(
                                          "/16-9-placeholder.jpg"
                                        )}
                                        fill
                                        className="object-cover"
                                        sizes={`10vw`}
                                      />
                                    )}
                                  </div>
                                  <div className="flex flex-col gap-y-xs">
                                    <p className="text-body">
                                      {page.item.attributes?.title}
                                    </p>
                                  </div>
                                </div>
                              </a>
                            </CommandItem>
                          ))}
                        </SearchSection>
                      ) : null}
                      {allUsers.length ? (
                        <SearchSection title="PROFILES">
                          {allUsers.map((user) => {
                            return (
                              <CommandItem
                                key={`user-${user.user_address}`}
                                value={`user-${user.user_address}`}
                                onSelect={() => {
                                  onSearchItemClick(
                                    `user-${user.user_address}`
                                  );
                                  router.push(getWalletPath(user.user_address));
                                }}
                              >
                                <a>
                                  <p className="text-body">
                                    {getUserDisplayName(user, {
                                      normalizedEnsName: normalizedEns,
                                      resolvedEnsAddress:
                                        ensAddress ?? undefined,
                                    })}
                                  </p>
                                </a>
                              </CommandItem>
                            );
                          })}
                        </SearchSection>
                      ) : null}
                    </>
                  );
                })()}
              </CommandList>
            </motion.div>
          </Popover.Content>
        ) : null}
      </AnimatePresence>
    </Popover.Portal>
  );
}

function getUserDisplayName(
  user: UserSearchResultFragment,
  ensData?: {
    normalizedEnsName?: string;
    resolvedEnsAddress?: string;
  }
): string {
  if (
    ensData?.normalizedEnsName &&
    ensData?.resolvedEnsAddress &&
    user.user_address === ensData.resolvedEnsAddress.toLowerCase()
  ) {
    return ensData.normalizedEnsName;
  }

  if (user.username) {
    return user.username;
  }

  return user.user_address;
}
